import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TrialSupportLink, TrialSupportLinkTranslation } from "../../../../../../core/models/trial-support-link.model";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { InputSelectComponent } from "../../../../../../shared/input-select/input-select.component";
import { ModalV2Component } from "../../../../../../shared/modal-v2/modal-v2.component";
import { Cultures } from "../../../../../../core/constants/cultures";
import { TrialSupportLinkService } from "../../../../../../core/services/trial-support-link.service";
import { BaseComponent } from "../../../../../../shared/base-component";
import { TrialDetail } from "../../../../../../core/models/trial-detail.model";
import { Countries } from "../../../../../../core/constants/countries";
import { SelectOption } from "../../../../../../core/models/select-option.model";
import { requiredNoWhitespaceValidator } from "../../../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-trial-support-link-form',
  templateUrl: './trial-support-link-form.component.html',
  styleUrl: './trial-support-link-form.component.scss'
})
export class TrialSupportLinkFormComponent extends BaseComponent {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;

  @Input() trial: TrialDetail;

  @Output() updated = new EventEmitter<TrialSupportLink[]>();

  supportLink: TrialSupportLink = new TrialSupportLink();

  form: FormGroup = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    selectedCulture: new FormControl<string>('en'),
    label: new FormControl<string>('', requiredNoWhitespaceValidator()),
    url: new FormControl<string>('', [requiredNoWhitespaceValidator(), Validators.pattern('^(https?:\\/\\/)?([\\da-z\\.-]+)\\.([a-z\\.]{2,6})([\\/\\w \\.-]*)*\\/?$')]),
    allCountries: new FormControl<boolean>(false),
    countries: new FormControl<string[]>([], this.countriesValidator())
  });

  cultureForms: FormGroup[] = [];
  availableCultures: { value: string, text: string }[] = [];
  filteredCultures: { value: string, text: string }[] = [];

  allCountryOptions: SelectOption[] = Countries.all().map(c => new SelectOption(c.code, c.name));
  filteredCountries: SelectOption[] = this.allCountryOptions;

  ngOnInit() {
  }

  constructor(private _trialSupportLinkService: TrialSupportLinkService) {
    super();

    Cultures.all().forEach(culture => {
      this.availableCultures.push({ value: culture.culture, text: culture.name });
    });
    this.filteredCultures = this.availableCultures;

    // Subscribe to allCountries changes to trigger validation
    this.form.get('allCountries').valueChanges.subscribe(() => {
      this.form.get('countries').updateValueAndValidity();
    });
  }

  buildCultureForms() {
    // Build culture forms
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = this.supportLink.translations.find(t => t.culture === culture.value);
      if (culture.value !== 'en') {
        this.cultureForms.push(new FormGroup({
          id: new FormControl<string>(translation?.id),
          culture: new FormControl<string>(culture.value),
          label: new FormControl<string>(translation?.label)
        }));
      }
    });
  }

  add() {
    this.supportLink = new TrialSupportLink();

    this.form.patchValue({
      isBusy: false,
      selectedCulture: 'en',
      label: '',
      url: '',
      allCountries: false,
      countries: [],
    });

    this.buildCultureForms();
    this.form.markAsUntouched();

    this.modal.show();
  }

  update(link: TrialSupportLink) {
    this.supportLink = link;

    this.form.patchValue({
      allCountries: this.supportLink.allCountries,
      countries: this.supportLink.countries ? this.supportLink.countries.split(',') : [],
      label: this.supportLink.label,
      url: this.supportLink.url,
      isBusy: false
    });

    this.buildCultureForms();

    this.modal.show();
  }

  onCountryFiltered(value: string) {
    if (value && value !== '') {
      this.filteredCountries = this.allCountryOptions.sort((a, b) => {
        const aMatches = a.text.toLowerCase().includes(value.toLowerCase());
        const bMatches = b.text.toLowerCase().includes(value.toLowerCase());
        if (aMatches && !bMatches) {
          return -1; // Move `a` before `b`
        } else if (!aMatches && bMatches) {
          return 1; // Move `b` before `a`
        }
      });
      return;
    }

    this.filteredCountries = this.allCountryOptions.sort((a, b) => {
      return a.text.localeCompare(b.text);
    });
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }

  onSubmitForm() {
    if (!this.form.valid) {
      // Mark all controls as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key).markAsTouched();
      });
      return;
    }

    this.form.patchValue({isBusy: true});

    // Form is valid, proceed with submission
    this.supportLink.label = this.form.value.label;
    this.supportLink.url = this.form.value.url.startsWith('http://') || this.form.value.url.startsWith('https://')
      ? this.form.value.url
      : 'https://' + this.form.value.url;
    this.supportLink.allCountries = this.form.value.allCountries;

    if (this.form.value.countries.length > 0) {
      this.supportLink.countries = this.form.value.countries.join(',');
    }

    if (!this.supportLink.translations)
      this.supportLink.translations = [];

    this.cultureForms.forEach(cultureForm => {
      const translation = this.supportLink.translations.find(t => t.culture == cultureForm.value.culture);
      if (!translation) {
        this.supportLink.translations.push(new TrialSupportLinkTranslation({
          culture: cultureForm.value.culture,
          label: cultureForm.value.label
        }));
      } else {
        translation.label = cultureForm.value.label;
      }
    });

    if (this.supportLink.id) {
      // Update
      this._trialSupportLinkService.update(this.trial.id, this.supportLink.id, this.supportLink).subscribe({
        next: links => {
          this.form.patchValue({isBusy: false});
          this._alertService.showSuccessAlert('Support link updated successfully');
          this.updated.emit(links);
          this.modal.hide();
        },
        error: err => {
          console.log(err);
          this.form.patchValue({isBusy: false});
          this._alertService.showErrorAlert(err);
        }
      });
    } else {
      // Add
      this._trialSupportLinkService.add(this.trial.id, this.supportLink).subscribe({
        next: links => {
          this.form.patchValue({isBusy: false});
          this._alertService.showSuccessAlert('Support link added successfully');
          this.updated.emit(links);
          this.modal.hide();
        },
        error: err => {
          console.log(err);
          this.form.patchValue({isBusy: false});
          this._alertService.showErrorAlert(err);
        }
      });
    }
  }

  /**
   * Validator for countries field that checks if at least one country is selected
   * when allCountries is false
   */
  private countriesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent;
      if (!parent) return null;

      const allCountries = parent.get('allCountries')?.value;
      const countries = control.value as string[];

      // If allCountries is false and countries array is empty or null
      if (!allCountries && (!countries || countries.length === 0)) {
        return { requiredCountries: true };
      }

      return null;
    };
  }
}
